import React, { useEffect, useState, useRef, useImperativeHandle } from "react";
import styles from "./Step2.module.css";
import { getRetellVoices } from "../../../Store/apiStore";
import { isPythonBackend, getPythonVoices } from "../../../utils/apiBackend";
import { defaultLanguage } from "../../BusinessStep/StepThree";
import PopUp from "../../Popup/Popup";
const Step2 = ({ onValidationChange, originalGender, originalVoice }) => {
  const [selectedGender, setSelectedGender] = useState("");
  const [prevAgentGender, setprevAgentGender] = useState("");
  const [selectedVoice, setSelectedVoice] = useState("");
  const [listVoices, setListVoices] = useState([]);
  const [filteredVoices, setFilteredVoices] = useState([]);
  const audioRefs = useRef([]);
  const [playingIdx, setPlayingIdx] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const [scale, setScale] = useState(1);
  const [isGenderChanging, setIsGenderChanging] = useState(false);
  const [isVoiceDirty, setIsVoiceDirty] = useState(false);

  const handleGenderChange = (gender) => {
    if (gender !== selectedGender) {
      if (playingIdx !== null) {
        const currentAudio = audioRefs.current[playingIdx];
        currentAudio?.pause();
        currentAudio.currentTime = 0;
      }

      setPlayingIdx(null);
      audioRefs.current = [];

      setSelectedGender(gender);
      setIsVoiceDirty(false);
      setIsGenderChanging(true);

      const dirty = gender.toLowerCase() !== originalGender?.toLowerCase();

      onValidationChange?.({
        // `gender` bhi bhejo. Parent pehle sessionStorage se compare karke tay
        // karta tha ki gender badla ya nahi, par wo value neeche wale effect me
        // sirf tab likhi jaati hai jab voice list aa chuki ho — list khaali ya
        // dhimi hui to parent ko lagta tha gender badla hi nahi, guard skip ho
        // jaata, aur agent nayi gender + purani gender ki voice ke saath save
        // ho jaata tha.
        gender,
        genderChanged: dirty,
        voiceSelected: false,
        isDirty: dirty,
      });
    }
  };

  useEffect(() => {
    if (
      localStorage.getItem("UpdationModeStepWise") === "ON" &&
      listVoices.length > 0
    ) {
      const storedGender = localStorage.getItem("agentGender");
      if (storedGender) {
        const formattedGender =
          storedGender.charAt(0).toUpperCase() +
          storedGender.slice(1).toLowerCase();
        setSelectedGender(formattedGender);
        sessionStorage.setItem("prevAgentGender", formattedGender);
      }

      const storedVoiceId = localStorage.getItem("agentVoice");
      if (storedVoiceId) {
        const matchingVoice = listVoices.find(
          (v) => v.voice_id === storedVoiceId
        );
        if (matchingVoice) {
          setSelectedVoice(matchingVoice);
        }
      }
    }
  }, [listVoices]);

  useEffect(() => {
    const fetchRetellVoiceList = async () => {
      try {
        // Python agent Fish Audio se bolta hai, Retell se nahi — us par Retell
        // ki voice id ("11labs-Cimo") lagane ka koi matlab nahi, wo call pe use
        // hi nahi hoti. Fish ki list agent ki apni language me maangi jaati hai
        // taaki ~1000 voices me se sirf kaam ki dikhein. Response Retell wali
        // shape me aata hai, is liye neeche ka poora filter/render waisa hi.
        const voiceResponses = isPythonBackend()
          ? await getPythonVoices({
              // Falls back to the site default, not a fixed "en-US": on the
              // India site that would have asked for Fish voices and shown a
              // list the Sarvam agent cannot use.
              language:
                sessionStorage.getItem("agentLanguageCode") ||
                defaultLanguage().locale,
            })
          : await getRetellVoices();

        setListVoices(voiceResponses.data);
      } catch (error) {
        console.error("Error fetching voices:", error);
      }
    };
    fetchRetellVoiceList();
  }, []);

  useEffect(() => {
    if (
      selectedVoice &&
      selectedVoice.voice_id &&
      selectedVoice.voice_name &&
      selectedVoice.accent &&
      !isGenderChanging
    ) {
      sessionStorage.setItem("agentVoice", selectedVoice.voice_id);
      sessionStorage.setItem("agentVoiceAccent", selectedVoice.accent);
      sessionStorage.setItem(
        "VoiceAgentName",
        selectedVoice?.voice_name?.replace(/\s*\(.*?\)/, "")
      );
    }
  }, [selectedVoice]);

  useEffect(() => {
    if (listVoices && selectedGender) {
      // Retell ki list me kai providers aate hain, unme se sirf "elevenlabs".
      // Python ki list server pehle hi sahi provider ki deta hai (Fish, Sarvam
      // ya naye .ai account ke liye ElevenLabs), is liye wahan provider filter
      // nahi — warna Sarvam/ElevenLabs ki saari voices chhant jaati.
      const filtered = listVoices.filter(
        (voice) =>
          (isPythonBackend() || voice.provider === "elevenlabs") &&
          voice.gender === selectedGender?.toLocaleLowerCase()
      );
      setFilteredVoices(filtered);
      sessionStorage.setItem("agentGender", selectedGender);
    }
  }, [selectedGender, listVoices]);

  useEffect(() => {
    if (isGenderChanging) {
      setTimeout(() => {
        setIsGenderChanging(false);
      }, 500); // Give it 500ms delay for safety
    }
  }, [isGenderChanging]);

  // play() returns a promise that REJECTS with AbortError when the clip is
  // paused before it has started — which is exactly what tapping a second voice
  // (or switching gender, or unmounting) does. Nothing awaited it, so the
  // rejection surfaced as an unhandled error and CRA threw the full-screen
  // "Uncaught runtime errors: The play() request was interrupted by a call to
  // pause()" overlay over the whole voice picker.
  const startPlayback = (audio) => {
    // A preview whose first load failed (the server was busy) stays failed in
    // the <audio> element — reload it so this tap fetches it again.
    if (audio.error || audio.networkState === audio.NETWORK_NO_SOURCE) audio.load();
    const started = audio.play();
    if (!started || typeof started.catch !== "function") return;
    started.catch((err) => {
      // Interrupting one preview with another is normal here; anything else
      // (autoplay blocked, missing file) is worth seeing in the console.
      if (err?.name !== "AbortError") {
        console.warn("Voice preview could not play:", err);
      }
    });
  };

  const togglePlay = (idx) => {
    const thisAudio = audioRefs.current[idx];
    if (!thisAudio) return;

    // if user taps the card that’s already playing → pause it
    if (playingIdx === idx) {
      thisAudio.pause();
      setPlayingIdx(null);
      return;
    }

    // pause whichever clip is currently playing
    if (playingIdx !== null) {
      const playingAudio = audioRefs.current[playingIdx];
      playingAudio?.pause();
      playingAudio.currentTime = 0;
    }

    // play the new clip
    startPlayback(thisAudio);
    setPlayingIdx(idx);

    // when it ends, clear the state so icons reset
    thisAudio.onended = () => setPlayingIdx(null);
  };

  const playAudio = (idx) => {
    const selectedAudio = audioRefs.current[idx];

    if (!selectedAudio) return;

    // Stop any currently playing audio
    if (playingIdx !== null && playingIdx !== idx) {
      const currentAudio = audioRefs.current[playingIdx];
      currentAudio?.pause();
      currentAudio.currentTime = 0;
    }

    // Play the selected one
    startPlayback(selectedAudio);
    setPlayingIdx(idx);

    selectedAudio.onended = () => setPlayingIdx(null);
  };
  useEffect(() => {
    if (listVoices.length > 0) {
      const storedGender = sessionStorage.getItem("agentGender");
      const storedVoiceId = sessionStorage.getItem("agentVoice");

      if (storedGender) {
        setSelectedGender(
          storedGender.charAt(0).toUpperCase() +
          storedGender.slice(1).toLowerCase()
        );
      }

      if (storedVoiceId) {
        const voice = listVoices.find((v) => v.voice_id === storedVoiceId);
        if (voice) {
          setSelectedVoice(voice);
        }
      }
    }
  }, [listVoices]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      let newScale = 1 - Math.min(scrollY / 400, 0.3);
      if (newScale < 0.7) newScale = 0.7;
      setScale(newScale);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <>
      <div className={styles.container}>
        <div className={styles.genderContainer}>
          {["Female", "Male"].map((gender) => (
            <label
              key={gender}
              className={`${styles.genderCard} ${selectedGender === gender ? styles.active : ""
                }`}
            >
              <span className={styles.icon}>
                {gender === "Female" ? (
                  <img src="svg/female-icon.svg" alt="Female Icon" />
                ) : (
                  <img src="svg/male-icon.svg" alt="Male Icon" />
                )}
              </span>
              <span>{gender}</span>
              <input
                type="radio"
                name="gender"
                value={gender}
                checked={selectedGender === gender}
                onChange={() => {
                  handleGenderChange(gender);
                }}
                className={styles.radioInput}
              />
            </label>
          ))}
        </div>

        {selectedGender && (
          <h2 className={styles.sectionTitle}>Select Voice</h2>
        )}
        <p className={styles.sectionmPara}>
          Select a voice for your agent as per your liking
        </p>
        <div className={styles.voiceGrid}>
          {filteredVoices.map((voice, idx) => (
            <label
              key={voice.voice_id ?? idx}
              className={`${styles.voiceCard} ${selectedVoice?.voice_name?.replace(/\s*\(.*?\)/, "") ===
                  voice?.voice_name?.replace(/\s*\(.*?\)/, "")
                  ? styles.active
                  : ""
                }`}
            >
              <input
                type="radio"
                name="voice"
                value={voice.voice_name}
                checked={
                  selectedVoice?.voice_name?.replace(/\s*\(.*?\)/, "") ===
                  voice?.voice_name?.replace(/\s*\(.*?\)/, "")
                }
                // onChange={() => {
                //   setSelectedVoice(voice);
                //   playAudio(idx);
                //   setIsVoiceDirty(true);

                //   const dirty = voice.voice_id != originalVoice;

                //   onValidationChange?.({
                //     genderChanged: false,
                //     voiceSelected: true,
                //     isDirty: dirty,
                //   });
                // }}
                onChange={() => {
                  setSelectedVoice(voice);
                  playAudio(idx);
                  setIsVoiceDirty(true);

                  // ✅ PEHLE: gender bhi check karo dirty ke liye
                  const currentGender = sessionStorage.getItem("agentGender") || "";
                  const prevGender = sessionStorage.getItem("prevAgentGender") || "";
                  const genderChanged = currentGender.toLowerCase() !== prevGender.toLowerCase();

                  // ✅ Voice dirty: original se alag hai YA gender change hua hai
                  const voiceDirty = voice.voice_id !== originalVoice || genderChanged;

                  onValidationChange?.({
                    genderChanged: genderChanged,
                    voiceSelected: true,
                    isDirty: voiceDirty,  // ✅ Ab sahi calculate hoga
                  });
                }}
                className={styles.radioInput}
              />
              <div
                className={styles.playIcon}
                onClick={(e) => {
                  e.preventDefault();
                  togglePlay(idx);
                  setSelectedVoice(voice);
                }}
                title="Preview"
              >
                <img
                  src={
                    playingIdx === idx
                      ? "svg/Pause-icon.svg"
                      : "svg/Play-icon.svg"
                  }
                  alt={playingIdx === idx ? "Play" : "Pause"}
                />
              </div>
              {/* Hidden audio element */}
              {/* preload="none": fetch a preview only when its play button is
                  tapped. Loading every card at once fired a burst of preview
                  requests and the busy voice service failed some of them, so
                  random voices stayed silent in some languages. */}
              <audio
                ref={(el) => (audioRefs.current[idx] = el)}
                style={{ display: "none" }}
                preload="none"
              >
                {/* No `type` on purpose: Fish previews are mp3, Sarvam previews are wav,
                    and a declared type the response does not match makes the browser
                    skip the source silently — the play button flips but nothing is
                    heard. Without it the browser uses the Content-Type we send. */}
                <source src={voice.preview_audio_url} />
              </audio>

              <div>
                <p className={styles.voiceName}>{voice.voice_name}</p>
                <p className={styles.voiceDesc}>{voice.accent} Accent</p>
              </div>
            </label>
          ))}
        </div>
      </div>
      {showPopup && (
        <PopUp
          type={popupType}
          onClose={() => setShowPopup(false)}
          message={popupMessage}
        />
      )}
    </>
  );
};

export default Step2;
